import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import paymentModeimg from "./assets/images/payment-mode.webp";
import { fetchCart } from "../store/cartSlice";

import "./ProductPage.css";

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);

  // Operational UI States
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartSubmitting, setCartSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState("tab1");
  const [relatedProducts, setRelatedProducts] = useState([]);

  const [activeIndex, setActiveIndex] = useState(null);
  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  const accordionData = [
    {
      title: "Shipping Information",
      contents:
        "Feel free to create additional blocks like this for Shipping, Payments, etc through the website. Also, you may easily adjust their order by dragging and dropping.",
    },
    {
      title: "Returns & Warranty",
      contents:
        "To return an item to us, follow the directions listed on the return exchange form included with your order. If you don’t have this form available, you can download one by clicking here",
    },
    ,
  ];

  // Helper: Get user id dynamically from localStorage to trigger redux sync
  const getUserId = () => {
    const userProfile = localStorage.getItem("user_profile");
    if (userProfile) {
      try {
        const parsed = JSON.parse(userProfile);
        return parsed.id || parsed.user_id;
      } catch (e) {
        console.error("Failed to parse user profile context", e);
        return null;
      }
    }
    return null;
  };

  const styles = {
    container: {
      maxWidth: "600px",
      margin: "20px auto",
      fontFamily: "system-ui, sans-serif",
      background: "#0f172a",
      padding: "20px",
      borderRadius: "12px",
      border: "1px solid #1e293b",
    },
    tabHeader: {
      display: "flex",
      gap: "10px",
      borderBottom: "2px solid  var(--main-third-color)",
      paddingBottom: "10px",
      marginBottom: "15px",
    },
    tabButton: (isActive) => ({
      border: "none",
      background: "transparent",
      color: isActive ? " var(--button-bg-color)" : "#94a3b8",
      borderBottom: isActive ? "3px solid  var(--button-bg-color)" : "none",
      padding: "10px 20px",
      cursor: "pointer",
      fontWeight: "bold",
    }),
    tabContent: {
      color: "#f8fafc",
      fontSize: "15px",
      lineHeight: "1.6",
      background: "#1e293b",
      padding: "16px",
      borderRadius: "8px",
      border: "1px solid #334155",
    },
  };

  // Fetch product layout details on mount/slug change
  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://rbuymart.rbhardwaj.com/server/api/products/get.php?slug=${slug}`,
        );
        const data = await response.json();

        if (!response.ok)
          throw new Error(data.message || "Failed to map product data assets.");

        setProduct(data.product);
        console.log(data.product);
        setQuantity(1); // ✅ FIXED: Route changes reset quantity back to 1 safely

        // Initialize gallery and variants arrays safely
        if (data.product.images?.length > 0) {
          const primary =
            data.product.images.find((img) => img.is_primary) ||
            data.product.images[0];
          setActiveImage(
            `https://rbuymart.rbhardwaj.com/server/${primary.image_path}`,
          );
        } else {
          setActiveImage("");
        }

        if (data.product.variants?.length > 0) {
          setSelectedVariant(data.product.variants[0]);
        } else {
          setSelectedVariant(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [slug]);

  // Fetch Storefront related products based on loaded product dependencies
  useEffect(() => {
    const fetchStorefrontData = async () => {
      if (!product || !product.sku) return;
      try {
        const productsRes = await fetch(
          "https://rbuymart.rbhardwaj.com/server/api/products/get_all.php",
        );
        const productsData = await productsRes.json();

        // ✅ FIXED: Safer array structural evaluation check
        const allProducts = Array.isArray(productsData)
          ? productsData
          : productsData.data || [];

        const related = allProducts
          .filter((p) => p.sku !== product.sku)
          .sort(() => Math.random() - 0.5)
          .slice(0, 4);

        setRelatedProducts(related);
      } catch (err) {
        console.error("Related products load failure:", err);
      }
    };

    fetchStorefrontData();
  }, [product?.sku]); // ✅ FIXED: Bound dependency target to primitive property to avoid re-fetch loops

  if (loading)
    return (
      <div className="product-view-loader">
        <div className="spinner"></div>
      </div>
    );
  if (error)
    return (
      <div className="product-view-error-container">
        <p>{error}</p>
        <button onClick={() => navigate("/")}>Return to Home</button>
      </div>
    );
  if (!product) return null;

  // Computed pricing and parameters
  const currentPrice = selectedVariant
    ? parseFloat(selectedVariant.price)
    : parseFloat(product.price);
  const currentStock = selectedVariant ? selectedVariant.stock : product.stock;

  const handleQuantityChange = (amount) => {
    const target = quantity + amount;
    if (target >= 1 && target <= currentStock) {
      setQuantity(target);
    }
  };

  const handleAddToCart = async (shouldRedirect = true) => {
    if (!token) {
      window.dispatchEvent(
        new CustomEvent("TRIGGER_AUTH_MODAL", { detail: { mode: "login" } }),
      );
      return false;
    }

    setCartSubmitting(true);
    try {
      // रिडक्स या लोकल स्टोरेज से यूजर आईडी निकालें (Fallback सुरक्षा के लिए)
      const userId = getUserId();

      const response = await fetch(
        "https://rbuymart.rbhardwaj.com/server/api/cart/add.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // बैकएंड इसी हेडर को रीड करेगा
          },
          body: JSON.stringify({
            user_id: userId,
            product_id: product.id,
            variant_id: selectedVariant ? selectedVariant.id : null,
            quantity: quantity,
          }),
        },
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Cart sync rejection.");

      // रिडक्स स्टोर सिंक करें
      if (userId) {
        dispatch(fetchCart(userId));
      }

      if (shouldRedirect) {
        navigate("/cart");
      }
      return true;
    } catch (err) {
      alert(`Error: ${err.message}`);
      return false;
    } finally {
      setCartSubmitting(false);
    }
  };

  const handleBuyNow = async () => {
    const success = await handleAddToCart(false);
    if (success) {
      navigate("/cart");
    }
  };
  // Dummy Data Array for Content Rendering
  const tabData = [
    { id: "tab1", label: "Description", content: product.description },
    {
      id: "tab2",
      label: "Specifications",
      content: `Weight: ${product.weight}kg | GST: ${product.gst_percent}% | SKU: ${product.sku}`,
    },
    {
      id: "tab3",
      label: "Reviews",
      content: "Customer reviews and ratings data matrices live here.",
    },
  ];

  return (
    <div className="product-detail-page">
      <div className="product-layout-grid section-div">
        {/* Visual Media Deck Block */}
        <div className="product-media-gallery">
          <div className="primary-hero-stage">
            <img
              src={activeImage || "https://via.placeholder.com/600"}
              alt={product.name}
            />
          </div>
          <div className="thumbnail-stripe">
            {product.images?.map((img, idx) => (
              <div
                key={idx}
                className={`thumb-wrapper ${activeImage.includes(img.image_path) ? "active" : ""}`}
                onClick={() =>
                  setActiveImage(
                    `https://rbuymart.rbhardwaj.com/server/${img.image_path}`,
                  )
                }
              >
                <img
                  src={`https://rbuymart.rbhardwaj.com/server/${img.image_path}`}
                  alt=""
                />
              </div>
            ))}
          </div>
        </div>

        {/* Meta Transaction Info Block */}
        <div className="product-specs-details">
          <span className="product-badge-sku">
            SKU: {selectedVariant ? selectedVariant.sku : product.sku}
          </span>
          <h1 className="product-core-title">{product.name}</h1>


          <div className="price-box">
            <h5>Price</h5>
            <section>
            
           
              
                <sup className="price-parsent">
                {(
                    ((product.compare_at_price - currentPrice) /
                      product.compare_at_price) *
                    100
                  ).toFixed(0)}
                  % OFF
                </sup>
            
              <h2 className="current-price">
                ₹{currentPrice.toLocaleString("en-IN")}
              </h2>
            </section>

            <section>
              <small>
                M.R.P. :
                <span className="old-price">
                  ₹{Number(product.compare_at_price).toLocaleString("en-IN")}
                </span>{" "}
              </small>
              <h4 className="save-price">
                (You save ₹
                {Math.max(
                  0,
                  Number(product.compare_at_price) - currentPrice,
                ).toLocaleString("en-IN")}
                )
              </h4>
            </section>
            <section>
             <div class="pdp-rating">
          <div class="pdp-stars">
            <span class="star filled">★</span><span class="star filled">★</span
            ><span class="star filled">★</span><span class="star filled">★</span
            ><span class="star half">★</span>
          </div>
          <span class="pdp-review-count">(8765)</span>
        </div>
            </section>
          </div>
 <h4>Product Details</h4>
          <p className="product-description-prose">
            {product.short_description}
          </p>

          {/* Conditional Variants Selector */}
          {product.variants?.length > 0 && (
            <div className="variants-selection-wrapper">
              <label className="section-label">Select Configuration</label>
              <div className="variants-chips-flex">
                {product.variants.map((variant) => (
                  <button
                    key={variant.id}
                    className={`variant-chip ${selectedVariant?.id === variant.id ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedVariant(variant);
                      setQuantity(1);
                    }}
                  >
                    {variant.title}
                  </button>
                ))}
              </div>
            </div>
          )}

         
          <div className="stock-status-badge">
            Availability :
            {currentStock > 0 ? (
              <span className="stock-in"> In Stock</span>
            ) : (
              <span className="stock-out"> Out of Stock</span>
            )}
          </div>

          <div class="payment-mode"><img loading="lazy" src={paymentModeimg} alt="visa icon"/></div>
          <div class="trusted-badges">
            <div class="trusted-badges-wrapper">
              <div class="trusted-badge">
                <i class="material-icons">&#xe897;</i>

                <small>
                  Privacy <br /> Protected
                </small>
              </div>
              <div class="trusted-badge">
                <i class="material-icons">&#xe8f8;</i>

                <small>
                  Premium
                  <br />
                  Quality
                </small>
              </div>
              <div class="trusted-badge">
                <i class="material-icons">&#xe32a;</i>

                <small>
                  Secure
                  <br />
                  Checkout
                </small>
              </div>
              <div class="trusted-badge">
                <i class="material-icons">&#xe558;</i>

                <small>
                  Cash on
                  <br />
                  Delivery
                </small>
              </div>
            </div>
          </div>

          {currentStock > 0 && (
            <div className="purchase-action-row">
              <div
                style={{ display: "flex", gap: "15px", alignItems: "center" }}
              >
                <div className="quantity-adjuster-deck">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span className="qty-value">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= currentStock}
                  >
                    +
                  </button>
                </div>
                <button
                  style={{
                    padding: "9px",
                    borderRadius: "5px",
                    border: "1px solid #0101013d",
                    cursor: "pointer",
                    background: "none",
                    color: "green",
                  }}
                  onClick={() => {
                    const pagelink = {
                      title: product.name,
                      text: "Check out this page!",
                      url: window.location.href,
                    };

                    if (navigator.share) {
                      navigator
                        .share(pagelink)
                        .then(() => console.log("Shared successfully"))
                        .catch((err) => console.log("Share failed:", err));
                    } else {
                      alert("Web Share API not supported!");
                    }
                  }}
                >
                  <i class="material-icons">&#xe80d;</i>
                </button>
              </div>

              <button
                className="btn-add-to-cart-action"
                onClick={() => handleAddToCart(true)}
                disabled={cartSubmitting}
              >
                <i class="material-icons">&#xe8cc;</i>
                {cartSubmitting ? "Loading..." : "Add to cart"}
              </button>

              <button
                className="btn-buynow-action"
                onClick={handleBuyNow}
                disabled={cartSubmitting}
              >
                <i class="material-icons">&#xe8c9;</i>
                {cartSubmitting ? "Loading..." : "Buy now"}
              </button>
            </div>
          )}



          <section className="Paccordion" id="Paccordion">
            {accordionData.map((item, index) => (
              <div className="Paccordion-item" key={index}>
                <div
                  className="Paccordion-header"
                  onClick={() => toggleAccordion(index)}
                >
                  {item.title}
                  <span>{activeIndex === index ? "-" : "+"}</span>
                </div>

                {activeIndex === index && (
                  <div className="Paccordion-content">{item.contents}</div>
                )}
              </div>
            ))}
          </section>

         
        </div>
      </div>

      <div className="description-box section-div">
        <div className="description-nav">
          {tabData.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="tab"
              style={styles.tabButton(activeTab === tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="tab-content">
          {tabData.find((tab) => tab.id === activeTab)?.content}
        </div>
      </div>

      <div className="rp-container section-div">
        <h3 className="rp-title">
          <span style={{ color: "var(--button-bg-color)",paddingRight:'5px' }}>
            Explore Related
          </span>
          Products
        </h3>

        <div className="rp-grid">
          {relatedProducts.map((item) => (
            <Link key={item.slug || item.id} to={`/product/${item.slug}`}>
              <div className="rp-card">
                <div className="rp-image-wrapper">
                  <img
                    src={`https://rbuymart.rbhardwaj.com/server/${item.primary_image}`}
                    alt={item.name}
                    className="rp-image"
                  />
                </div>
                <div className="rp-info">
                  <h3 className="rp-name">{item.name}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
